import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  IconButton,
  Slot,
  toast,
} from '../../../../core'
import { EncounterUpdateFailuresList } from './EncounterUpdateFailuresList'
import type { CurrentUserContextValue } from '../../../utils/currentUserContext'
import type { UpdateEncounterVariables } from '../hooks/useEncounterMutations.types'
import type { components } from '@joyful/openapi/types.gen'
import { pluralize } from '@joyful/utils'
import { CircleCheckIcon, CircleXIcon, XIcon } from 'lucide-react'

export interface EncounterBulkUpdateResultToastDismissProps {
  toastId: string | number
}

export function EncounterBulkUpdateResultToastDismiss({
  toastId,
}: EncounterBulkUpdateResultToastDismissProps) {
  return (
    <span className="self-start">
      <IconButton
        variant="ghost"
        size="sm"
        onClick={() => {
          toast.dismiss(toastId)
        }}
      >
        <XIcon />
      </IconButton>
    </span>
  )
}

export interface EncounterBulkUpdateResultToastContentProps {
  currentUser: CurrentUserContextValue
  result: components['schemas']['BulkEncounterUpdateResponse']
  variables: Pick<
    UpdateEncounterVariables,
    | 'assigneeBillerUser'
    | 'flagged_for_joyful_followup'
    | 'enrichment_status_override'
    | 'snoozed_until'
    | 'snoozed_until_is_indefinite'
    | 'workflow_status'
  >
}

export function EncounterBulkUpdateResultToastContent({
  currentUser,
  result,
  variables,
}: EncounterBulkUpdateResultToastContentProps) {
  return (
    <div className="flex gap-1.5">
      <Slot className="size-5">
        {result.failed_count > 0 &&
        result.updated_count === 0 &&
        result.noop_count === 0 ? (
          <CircleXIcon className="text-red-600" />
        ) : (
          <CircleCheckIcon className="text-green-600" />
        )}
      </Slot>
      <div className="flex flex-[1_0_0] flex-col gap-2">
        {result.updated_count > 0 || result.noop_count > 0 ? (
          <p>
            {buildBulkUpdateSuccessMessage({
              currentUser,
              result,
              variables,
            })}
          </p>
        ) : null}

        {result.failed_count > 0 ? (
          <Accordion type="single" collapsible>
            <AccordionItem value="failed-updates">
              <AccordionTrigger
                size="sm"
                variant={result.updated_count === 0 ? 'error' : 'warning'}
              >
                {`${pluralize(result.failed_count, 'encounter')} failed to update`}
              </AccordionTrigger>

              <AccordionContent padding="none">
                <div className="max-h-40 overflow-auto px-2 pt-2 font-normal">
                  <EncounterUpdateFailuresList
                    failures={result.results
                      .filter((item) => {
                        return item.success === false
                      })
                      .map((item) => {
                        return {
                          encounter: {
                            id: item.encounter.id,
                            patient_name: item.encounter.patient_name,
                            start_date: item.encounter.start_date,
                          },
                          violations: item.violations ?? [],
                        }
                      })}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : null}
      </div>
    </div>
  )
}

function buildBulkUpdateSuccessMessage({
  currentUser,
  result,
  variables,
}: EncounterBulkUpdateResultToastContentProps) {
  const { assigneeBillerUser } = variables

  const parts: Array<string> = []

  if (assigneeBillerUser !== undefined) {
    const targetName =
      assigneeBillerUser === null
        ? null
        : assigneeBillerUser.id === currentUser.id
          ? 'me'
          : assigneeBillerUser.name

    if (result.updated_count > 0) {
      parts.push(
        assigneeBillerUser === null
          ? `Unassigned biller assignee for ${pluralize(result.updated_count, 'encounter')}.`
          : `Set biller assignee for ${pluralize(result.updated_count, 'encounter')} to ${targetName}.`,
      )
    }

    if (result.noop_count > 0) {
      parts.push(
        assigneeBillerUser === null
          ? `${pluralize(result.noop_count, 'encounter')} already have no biller assignee.`
          : `${pluralize(result.noop_count, 'encounter')} already assigned to ${targetName}.`,
      )
    }

    return parts.join(' ')
  }

  if (variables.flagged_for_joyful_followup !== undefined) {
    if (result.updated_count > 0) {
      parts.push(
        variables.flagged_for_joyful_followup === true
          ? `Flagged ${pluralize(result.updated_count, 'encounter')} for Joyful Health follow-up.`
          : `Unflagged ${pluralize(result.updated_count, 'encounter')} for Joyful Health follow-up.`,
      )
    }

    if (result.noop_count > 0) {
      parts.push(
        variables.flagged_for_joyful_followup === true
          ? `${pluralize(result.noop_count, 'encounter')} already flagged for Joyful Health follow-up.`
          : `${pluralize(result.noop_count, 'encounter')} already unflagged for Joyful Health follow-up.`,
      )
    }

    return parts.join(' ')
  }

  if (variables.enrichment_status_override !== undefined) {
    if (result.updated_count > 0) {
      parts.push(
        variables.enrichment_status_override === null
          ? `Removed status override for ${pluralize(result.updated_count, 'encounter')}.`
          : `Set status override for ${pluralize(result.updated_count, 'encounter')}.`,
      )
    }

    if (result.noop_count > 0) {
      parts.push(
        variables.enrichment_status_override === null
          ? `${pluralize(result.noop_count, 'encounter')} already have no status override.`
          : `${pluralize(result.noop_count, 'encounter')} already have this status override.`,
      )
    }

    return parts.join(' ')
  }

  if (variables.workflow_status !== undefined) {
    if (result.updated_count > 0) {
      parts.push(
        variables.workflow_status === null
          ? `Removed workflow status for ${pluralize(result.updated_count, 'encounter')}.`
          : `Changed workflow status for ${pluralize(result.updated_count, 'encounter')}.`,
      )
    }

    if (result.noop_count > 0) {
      parts.push(
        variables.workflow_status === null
          ? `${pluralize(result.noop_count, 'encounter')} already have no workflow status.`
          : `${pluralize(result.noop_count, 'encounter')} already have this workflow status.`,
      )
    }

    return parts.join(' ')
  }

  if (variables.snoozed_until !== undefined) {
    const isUnsnooze = variables.snoozed_until === null

    if (result.updated_count > 0) {
      parts.push(
        isUnsnooze
          ? `Unsnoozed ${pluralize(result.updated_count, 'encounter')}.`
          : `Snoozed ${pluralize(result.updated_count, 'encounter')}.`,
      )
    }

    if (result.noop_count > 0) {
      parts.push(
        isUnsnooze
          ? `${pluralize(result.noop_count, 'encounter')} already unsnoozed.`
          : `${pluralize(result.noop_count, 'encounter')} already snoozed.`,
      )
    }

    return parts.join(' ')
  }

  if (result.updated_count > 0) {
    parts.push(`Updated ${pluralize(result.updated_count, 'encounter')}.`)
  }

  if (result.noop_count > 0) {
    parts.push(
      `${pluralize(result.noop_count, 'encounter')} already have this value.`,
    )
  }

  return parts.join(' ')
}
